import { useCallback, useMemo } from "react";
import { useNavigate, type To } from "react-router-dom";

import type { BreadcrumbItem } from "./Breadcrumbs";

type Fallback =
  | {
      to: To;
      state?: unknown;
    }
  | (() => void);

interface UseBreadcrumbBackOptions {
  items?: BreadcrumbItem[];
  includeHome?: boolean;
  homeLabel?: string;
  homeTo?: To;
  homeState?: unknown;
  fallback?: Fallback;
}

export function useBreadcrumbBack({
  items,
  includeHome = true,
  homeLabel = "Home",
  homeTo = "/dashboard",
  homeState,
  fallback,
}: UseBreadcrumbBackOptions = {}) {
  const navigate = useNavigate();

  const target = useMemo(() => {
    if (!items || items.length === 0) {
      return includeHome ? { label: homeLabel, to: homeTo, state: homeState } : null;
    }

    const resolvedItems = includeHome
      ? [{ label: homeLabel, to: homeTo, state: homeState }, ...items]
      : items;

    if (resolvedItems.length < 2) {
      return null;
    }

    for (let index = resolvedItems.length - 2; index >= 0; index -= 1) {
      const candidate = resolvedItems[index];
      if (candidate.to) {
        return candidate;
      }
    }
    return null;
  }, [homeLabel, homeState, homeTo, includeHome, items]);

  return useCallback(() => {
    if (target?.to) {
      navigate(target.to, { state: target.state });
      return;
    }

    if (typeof fallback === "function") {
      fallback();
      return;
    }

    if (fallback) {
      navigate(fallback.to, { state: fallback.state });
      return;
    }

    navigate(-1);
  }, [fallback, navigate, target]);
}
